import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { SITE_URL } from "@/lib/brand";
import DOMPurify from "dompurify";
import NotFound from "@/pages/NotFound";

const PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    "p","br","hr","div","span",
    "h1","h2","h3","h4","h5","h6",
    "strong","b","em","i","u","s","strike","sub","sup","small","mark",
    "ul","ol","li","blockquote","pre","code","a",
  ],
  ALLOWED_ATTR: ["href","title","target","rel","style","class"],
  ALLOW_DATA_ATTR: false,
};

export default function Page() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, page: null });

  useEffect(() => {
    setState({ loading: true, page: null });
    api.get(`/pages/${slug}`).then((r) => setState({ loading: false, page: r.data }))
      .catch(() => setState({ loading: false, page: null }));
  }, [slug]);

  useEffect(() => {
    const p = state.page;
    if (!p) return;
    document.title = p.seo_title || `${p.title} — Golden Moth Market`;
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.setAttribute("name", "description"); document.head.appendChild(m); }
    m.setAttribute("content", p.seo_description || "");
    let r = document.querySelector('meta[name="robots"]');
    if (!r) { r = document.createElement("meta"); r.setAttribute("name", "robots"); document.head.appendChild(r); }
    r.setAttribute("content", p.indexable ? "index,follow" : "noindex,nofollow");
    let c = document.querySelector('link[rel="canonical"]');
    if (!c) { c = document.createElement("link"); c.setAttribute("rel", "canonical"); document.head.appendChild(c); }
    if (typeof window !== "undefined") c.setAttribute("href", SITE_URL + window.location.pathname);
  }, [state.page]);

  if (state.loading) return <div className="text-center py-32 font-caption text-brass">Turning the page…</div>;
  if (!state.page) return <NotFound />;

  const p = state.page;
  const raw = (p.content || "").trim();
  // Backward-compat: pages saved as plain text before rich text arrived — auto-wrap paragraphs on blank lines.
  const looksLikeHTML = /<[a-z][\s\S]*?>/i.test(raw);
  const html = looksLikeHTML
    ? raw
    : raw.split(/\n{2,}/).map((s) => `<p>${s.replace(/\n/g, "<br>")}</p>`).join("");
  const clean = DOMPurify.sanitize(html, PURIFY_CONFIG);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24" data-testid={`page-${p.slug}`}>
      <div className="text-center mb-10 sm:mb-14">
        <p className="font-caption text-brass text-xs mb-2">Proprietor's Ledger</p>
        <h1 className="font-display text-4xl sm:text-6xl text-gold leading-[1.05]" data-testid="page-title">{p.heading || p.title}</h1>
      </div>
      {!raw ? (
        <p className="text-parchment/60 italic text-center">Content coming shortly.</p>
      ) : (
        <div className="prose-page" data-testid="page-content" dangerouslySetInnerHTML={{ __html: clean }} />
      )}
    </div>
  );
}
